<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import MessageApi from 'dashboard/api/inbox/message.js';
import MessageList from 'next/message/MessageList.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const MESSAGE_PAGE_SIZE = 20;
const conversationMessagesCache = new Map();

const CHANNEL_LABELS = {
  'Channel::Api': 'API',
  'Channel::Email': 'Email',
  'Channel::FacebookPage': 'Facebook',
  'Channel::Telegram': 'Telegram',
  'Channel::TwilioSms': 'SMS',
  'Channel::TwilioWhatsapp': 'WhatsApp',
  'Channel::Whatsapp': 'WhatsApp',
  'Channel::WebWidget': 'Website',
};

const EMPTY_REPLY_TO_SUPPORT = { incoming: false, outgoing: false };

const props = defineProps({
  contactId: {
    type: [Number, String],
    default: null,
  },
  currentConversationId: {
    type: Number,
    required: true,
  },
  currentInboxName: {
    type: String,
    default: '',
  },
  currentUserId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['loaded']);

const store = useStore();
const isExpanded = ref(true);
const isLoading = ref(false);
const errorMessage = ref('');
const sections = ref([]);

let requestSequence = 0;

const allContactConversations = computed(() => {
  const contactId = Number(props.contactId);

  if (!contactId) {
    return [];
  }

  return (
    store.getters['contactConversations/getAllConversationsByContactId'](
      contactId
    ) || []
  );
});

const otherConversations = computed(() =>
  allContactConversations.value.filter(
    conversation =>
      Number(conversation.id) !== Number(props.currentConversationId)
  )
);

const formatTimestamp = timestamp => {
  if (!timestamp) {
    return 'Recent activity';
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(Number(timestamp) * 1000));
};

const getInbox = inboxId => store.getters['inboxes/getInbox'](inboxId) || {};

const getChannelLabel = conversation => {
  const channelType =
    conversation?.meta?.channel || getInbox(conversation?.inboxId)?.channel_type;

  if (!channelType) {
    return 'Channel';
  }

  return (
    CHANNEL_LABELS[channelType] || channelType.replace('Channel::', '') || 'Channel'
  );
};

const getInboxName = conversation => {
  const inbox = getInbox(conversation?.inboxId);
  return inbox?.name || getChannelLabel(conversation);
};

const isEmailConversation = conversation => {
  return (conversation?.meta?.channel || '').includes('Email');
};

const fetchConversationMessages = async conversationId => {
  if (conversationMessagesCache.has(conversationId)) {
    return conversationMessagesCache.get(conversationId);
  }

  let before;
  let messages = [];

  while (true) {
    const { data } = await MessageApi.getPreviousMessages({
      conversationId,
      before,
    });

    const payload = data?.payload || [];

    if (!payload.length) {
      break;
    }

    messages = payload.concat(messages);

    const oldestMessageId = payload[0]?.id;

    if (
      payload.length < MESSAGE_PAGE_SIZE ||
      !oldestMessageId ||
      Number(oldestMessageId) === Number(before)
    ) {
      break;
    }

    before = oldestMessageId;
  }

  conversationMessagesCache.set(conversationId, messages);
  return messages;
};

const emitLoaded = () => {
  emit('loaded', { hasHistory: sections.value.length > 0 });
};

const loadHistory = async () => {
  const contactId = Number(props.contactId);
  const currentConversationId = Number(props.currentConversationId);
  const sequence = ++requestSequence;

  sections.value = [];
  errorMessage.value = '';

  if (!contactId || !currentConversationId) {
    emitLoaded();
    return;
  }

  isLoading.value = true;

  try {
    await store.dispatch('contactConversations/get', contactId);

    if (sequence !== requestSequence) {
      return;
    }

    const conversations = otherConversations.value;

    if (!conversations.length) {
      sections.value = [];
      return;
    }

    const loadedSections = await Promise.all(
      conversations.map(async conversation => {
        const messages = await fetchConversationMessages(Number(conversation.id));

        return {
          ...conversation,
          messages,
          inboxName: getInboxName(conversation),
          channelLabel: getChannelLabel(conversation),
          isEmailChannel: isEmailConversation(conversation),
          lastActivityLabel: formatTimestamp(conversation.lastActivityAt),
          sortTimestamp:
            messages[0]?.created_at ||
            messages[0]?.createdAt ||
            conversation.createdAt ||
            conversation.lastActivityAt ||
            0,
        };
      })
    );

    if (sequence !== requestSequence) {
      return;
    }

    sections.value = loadedSections
      .filter(section => section.messages.length > 0)
      .sort((left, right) => Number(left.sortTimestamp) - Number(right.sortTimestamp));
  } catch (error) {
    if (sequence !== requestSequence) {
      return;
    }

    errorMessage.value =
      'All-channel history had a small existential crisis. Reload this conversation and it should recover.';
  } finally {
    if (sequence !== requestSequence) {
      return;
    }

    isLoading.value = false;
    emitLoaded();
  }
};

watch(
  [() => props.contactId, () => props.currentConversationId],
  () => {
    loadHistory();
  },
  { immediate: true }
);
</script>

<template>
  <li
    v-if="isLoading && !sections.length"
    class="list-none px-4 pt-4"
  >
    <div
      class="flex items-center gap-3 px-4 py-3 rounded-2xl border border-n-weak bg-n-surface-2 text-sm text-n-slate-11"
    >
      <Spinner class="text-n-brand" />
      <span>Loading all-channel history for this contact...</span>
    </div>
  </li>

  <li
    v-if="errorMessage"
    class="list-none px-4 pt-4"
  >
    <div
      class="px-4 py-3 rounded-2xl border border-n-ruby-7/40 bg-n-ruby-3/10 text-sm text-n-ruby-11"
    >
      {{ errorMessage }}
    </div>
  </li>

  <template v-if="sections.length">
    <li class="list-none px-4 pt-4">
      <div class="rounded-2xl border border-n-weak bg-n-surface-2 overflow-hidden">
        <button
          class="w-full flex items-start justify-between gap-4 px-4 py-3 text-left"
          type="button"
          @click="isExpanded = !isExpanded"
        >
          <div class="min-w-0">
            <p
              class="text-[0.7rem] font-semibold uppercase tracking-[0.24em] text-n-slate-11"
            >
              All Channels History
            </p>
            <p class="mt-1 text-sm text-n-slate-11 leading-6">
              {{ sections.length }}
              {{
                sections.length === 1
                  ? 'previous conversation'
                  : 'previous conversations'
              }}
              in one scroll. Read above, reply below, avoid channel roulette.
            </p>
          </div>
          <span
            class="shrink-0 inline-flex items-center px-3 py-1 rounded-full border border-n-weak bg-n-surface-1 text-xs font-semibold text-n-slate-11"
          >
            {{ isExpanded ? 'Hide' : 'Show' }}
          </span>
        </button>
      </div>
    </li>

    <template v-if="isExpanded">
      <li
        v-for="section in sections"
        :key="section.id"
        class="list-none px-4 pt-4"
      >
        <section
          class="overflow-hidden rounded-2xl border border-n-weak bg-n-surface-1 shadow-sm"
        >
          <header
            class="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-n-weak bg-n-surface-2"
          >
            <span
              class="inline-flex items-center px-2.5 py-1 rounded-full bg-n-brand/10 text-n-brand text-[0.7rem] font-semibold uppercase tracking-[0.18em]"
            >
              {{ section.channelLabel }}
            </span>
            <span class="text-sm font-semibold text-n-slate-12">
              {{ section.inboxName }}
            </span>
            <span class="text-xs text-n-slate-11">
              #{{ section.displayId }}
            </span>
            <span class="ml-auto text-xs text-n-slate-11">
              {{ section.lastActivityLabel }}
            </span>
          </header>

          <MessageList
            class="!bg-transparent !px-0"
            :current-user-id="currentUserId"
            :is-an-email-channel="section.isEmailChannel"
            :inbox-supports-reply-to="EMPTY_REPLY_TO_SUPPORT"
            :messages="section.messages"
          />
        </section>
      </li>

      <li class="list-none px-4 pt-5 pb-2">
        <div class="relative flex items-center justify-center">
          <div class="absolute inset-x-0 h-px bg-n-weak" />
          <span
            class="relative z-10 px-3 py-1 rounded-full border border-n-weak bg-n-surface-1 text-[0.7rem] font-semibold uppercase tracking-[0.24em] text-n-slate-11"
          >
            Current Channel · {{ currentInboxName || 'Conversation' }}
          </span>
        </div>
      </li>
    </template>
  </template>
</template>
